import type { ComponentType, ReactNode } from 'react';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom';
import { ClassCodeGatePage } from '../components/ClassCodeGatePage';
import { RequireUnlocked } from '../components/RequireUnlocked';
import { NotFound } from '../components/NotFound';
import { RouteError } from '../components/RouteError';
import { SiteShell } from '../site/SiteShell';

const locked = (el: ReactNode) => <RequireUnlocked>{el}</RequireUnlocked>;
const to = (path: string) => <Navigate to={path} replace />;

// Each page's code is downloaded when its route is first opened (Astra's review, Nov 2 2026: every page, the class
// code gate and the privacy notice included, used to download the host tools, the creator and the games first).
// The router waits for the page's code before it shows the page, so there's no half-drawn screen in between.
// `gate`: behind the class code.
function page<M>(load: () => Promise<M>, pick: (m: M) => ComponentType, gate = false) {
  return async () => {
    const Page = pick(await load());
    return { element: gate ? locked(<Page />) : <Page /> };
  };
}

const router = createBrowserRouter([
  // Open to anyone, with no class code: Google's sign-in screen links to it.
  { path: '/privacy', lazy: page(() => import('../site/pages/PrivacyPage'), (m) => m.PrivacyPage), errorElement: <RouteError /> },
  {
    path: '/unlock',
    element: <ClassCodeGatePage />,
    errorElement: <RouteError />,
  },

  // Live games run full screen, outside the site's rail.
  { path: '/play/live', lazy: page(() => import('../game/PlayPage'), (m) => m.PlayPage, true), errorElement: <RouteError /> },
  // The host page has its own teacher password, so the teacher never needs a class code to reach it.
  { path: '/play/host', lazy: page(() => import('../game/HostPage'), (m) => m.HostPage), errorElement: <RouteError /> },
  { path: '/play/slime', lazy: page(() => import('../game/SlimeCreatorPage'), (m) => m.SlimeCreatorPage, true), errorElement: <RouteError /> },
  // solo Meteor is on the TV in my room now; an old link to it goes there
  { path: '/play/meteor', element: <Navigate to="/play/room" replace state={{ tv: true }} />, errorElement: <RouteError /> },
  { path: '/play/room', lazy: page(() => import('../game/HomePage'), (m) => m.HomePage, true), errorElement: <RouteError /> },
  // Lantern Line's grey-box prototype, to try before the art is made (not linked from anywhere yet)
  { path: '/play/lantern', lazy: page(() => import('../game/LanternLinePage'), (m) => m.LanternLinePage, true), errorElement: <RouteError /> },
  // the purikura booth on its own test stage, until the world has a place for it
  { path: '/play/booth', lazy: page(() => import('../game/BoothPage'), (m) => m.BoothPage, true), errorElement: <RouteError /> },
  // Restock (品出し), a prototype to play before the class version (not linked from anywhere yet)
  { path: '/play/restock', lazy: page(() => import('../game/RestockPage'), (m) => m.RestockPage, true), errorElement: <RouteError /> },

  {
    path: '/',
    element: locked(<SiteShell />),
    errorElement: <RouteError />,
    children: [
      { index: true, element: to('/board') },
      { path: 'board', lazy: page(() => import('../site/pages/BoardPage'), (m) => m.BoardPage) },
      { path: 'schedule', lazy: page(() => import('../site/pages/SchedulePage'), (m) => m.SchedulePage) },
      { path: 'materials', lazy: page(() => import('../site/pages/MaterialsPage'), (m) => m.MaterialsPage) },
      { path: 'practice', lazy: page(() => import('../site/pages/PracticePage'), (m) => m.PracticePage) },
      { path: 'practice/teap/part-1', lazy: page(() => import('../components/teap/TeapPart1'), (m) => m.TeapPart1) },
      { path: 'practice/teap/part-2', lazy: page(() => import('../components/teap/TeapSpeaking'), (m) => m.TeapSpeaking) },
      { path: 'practice/teap/part-3', lazy: page(() => import('../components/teap/TeapPart3'), (m) => m.TeapPart3) },
      { path: 'practice/teap/part-4', lazy: page(() => import('../components/teap/TeapPart4'), (m) => m.TeapPart4) },
      { path: 'play', lazy: page(() => import('../site/pages/PlaySection'), (m) => m.PlaySection) },

      // Old addresses students may have saved
      { path: 'dashboard', element: to('/board') },
      { path: 'class-materials/*', element: to('/materials') },
      { path: 'resources/*', element: to('/materials') },
      { path: 'test-prep/*', element: to('/practice') },
      { path: 'flashcards/*', element: to('/practice') },
      { path: 'lexical-coach', element: to('/practice') },
      { path: 'teap/speaking/part-1', element: to('/practice/teap/part-1') },
      { path: 'teap/speaking/part-2', element: to('/practice/teap/part-2') },
      { path: 'teap/speaking/part-3', element: to('/practice/teap/part-3') },
      { path: 'teap/speaking/part-4', element: to('/practice/teap/part-4') },
      { path: 'teap/*', element: to('/practice') },
      { path: '*', element: <NotFound /> },
    ],
  },
]);

export function AppRouter() {
  return <RouterProvider router={router} />;
}
